<template>
  <icon-button
    type="button"
    class="-ico-only -no-container"
    :class="{
      '-light': !isSchedulable,
    }"
    icon="calendar_today"
    label="Add to Calendar"
    @click-action="openModal"
  />
</template>

<script lang="ts">
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import { mapState, mapActions } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { useModalsStore } from '@/stores/modals'
import IconButton from '@/components/buttons/icon-button.vue'
import type { PlusPass } from '@/types/search-types'

/**
 * Add to calendar button
 */
export default defineComponent({
  name: 'CalendarButton',
  components: { IconButton },
  props: {
    entityId: {
      required: true,
      type: Number,
    },
    entityType: {
      default: 'workout',
      type: String,
      validator: (val: string) =>
        ['workout', 'custom_workout', 'plan', 'article', 'wellness_video'].includes(val),
    },
    // differentiate between plan types: programs, challenges, routines
    entityCategory: {
      default: '',
      validator: (val: string) => ['', 'program', 'challenge', 'routine'].includes(val),
    },
    entityTitle: {
      default: '',
      type: String,
    },

    // props only needed if plus pass modal will be shown, otherwise leave defaults
    isExclusive: {
      type: Boolean,
      default: false,
    },
    isSchedulable: {
      type: Boolean,
      default: true,
    },
    plusPass: {
      type: Object as PropType<PlusPass | null>,
      default: null,
    },
    cheapestMonthlyPrice: {
      type: String,
      default: null,
    },
    imageUrlPrefix: {
      type: String,
      default: '',
    },
    image: {
      type: String,
      default: '',
    },
    // end plus pass modal props
  },
  data() {
    return {}
  },
  computed: {
    ...mapState(useUserAuthStore, ['user', 'features', 'cdn']),
  },
  methods: {
    ...mapActions(useModalsStore, ['showLoginModal', 'openSimpleModal']),

    // calendar or plus pass modal
    openModal() {
      if (!this.user.isLoggedIn && !this.isExclusive) {
        this.showLoginModal()
        return
      }

      if (!this.isSchedulable && this.entityCategory === 'routine') {
        return this.openSimpleModal({
          type: 'STANDARD',
          id: `routine-publish-${this.entityId}-modal`,
          props: {
            heading: 'Routine Draft',
            showFooter: false,
            bodySlot:
              'Add workouts or custom workouts to your routine. When ready, finish and launch the routine to enable adding to your calendar.',
          },
        })
      }

      if (!this.isSchedulable && !this.plusPass?.id) {
        return
      }

      // open plus pass modal
      if (!this.isSchedulable && this.plusPass!.id) {
        // use image from props first
        let passImagePrefix = this.imageUrlPrefix
        let passImage = this.image

        // default to pass image if one isn't passed in
        if (!passImagePrefix) {
          passImagePrefix = `${this.cdn.siteAssetsPrefix || '/siteassets'}/${
            this.plusPass!.img_dir
          }`
          passImage = this.plusPass!.image
        }

        return this.openSimpleModal({
          type: 'PASS_PURCHASE',
          id: `plus-pass-${this.entityType}-${this.entityId}-modal`,
          props: {
            entityId: this.entityId,
            entityType: this.entityType,
            plusPass: this.plusPass,
            cheapestMonthlyPrice: this.cheapestMonthlyPrice,
            imageUrlPrefix: passImagePrefix,
            image: passImage,
          },
        })
      }

      // schedulable
      // this is the default if isSchedulable and isExclusive is not passed in
      this.openSimpleModal({
        type: 'CALENDAR_ADD',
        id: `calendar-add-${this.entityType}-${this.entityId}-modal`,
        props: {
          entityId: this.entityId,
          entityType: this.entityType,
          entityTitle: this.entityTitle,
          entityCategory: this.entityCategory,
        },
      })
    },
  },
})
</script>
